import { useEffect } from 'react';
import { X, TrendingDown, Layers } from 'lucide-react';
import type { PLVProduct } from '../types/plv.types';
import { formatPrice } from '../utils/pricingUtils';

interface PricingTiersModalProps {
  product: PLVProduct;
  onClose: () => void;
}

export function PricingTiersModal({ product, onClose }: PricingTiersModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  if (!product.quantityPricePairs || product.quantityPricePairs.length === 0) return null;

  const sortedPairs = [...product.quantityPricePairs].sort((a, b) => a.quantity - b.quantity);

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50"
      onClick={onClose}
    >
      <div
        className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-lg tw-mx-4 tw-max-h-[90vh] tw-overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-start tw-justify-between tw-px-6 tw-pt-5 tw-pb-2">
          <div>
            <h5 className="tw-text-base tw-font-semibold tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
              <TrendingDown className="tw-w-5 tw-h-5" />
              Prix dégressifs
            </h5>
            <p className="tw-text-sm tw-text-gray-500 tw-mt-0.5">{product.name}</p>
          </div>
          <button
            className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-transition-colors tw-text-gray-400 hover:tw-text-gray-600"
            onClick={onClose}
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Table */}
        <div className="tw-px-6 tw-py-4 tw-overflow-auto">
          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
            <div className="tw-bg-gray-50 tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-200">
              <h6 className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2 tw-text-gray-600">
                <Layers className="tw-w-4 tw-h-4" />
                Quantités et prix
              </h6>
            </div>
            <table className="tw-w-full tw-text-sm">
              <thead>
                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                  <th className="tw-px-4 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-600">Quantité</th>
                  <th className="tw-px-4 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-600">Prix du lot HT</th>
                  <th className="tw-px-4 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-600">Prix unitaire</th>
                  <th className="tw-px-4 tw-py-2.5 tw-text-left tw-font-medium tw-text-gray-600">Réduction</th>
                </tr>
              </thead>
              <tbody>
                {sortedPairs.map((pair, index) => {
                  const isFirst = index === 0;
                  const unitPrice = pair.price / pair.quantity;
                  let percentageSavings = 0;
                  if (!isFirst) {
                    const baseUnitPrice = sortedPairs[0].price / sortedPairs[0].quantity;
                    percentageSavings = ((baseUnitPrice - unitPrice) / baseUnitPrice) * 100;
                  }
                  return (
                    <tr key={index} className="tw-border-b tw-border-gray-100 last:tw-border-0 hover:tw-bg-gray-50">
                      <td className="tw-px-4 tw-py-3 tw-font-semibold">
                        {pair.quantity.toLocaleString('fr-FR')}
                      </td>
                      <td className="tw-px-4 tw-py-3 tw-font-semibold tw-text-affinicar-secondary">
                        {formatPrice(pair.price)}
                      </td>
                      <td className="tw-px-4 tw-py-3 tw-font-medium tw-text-affinicar-secondary">
                        {formatPrice(unitPrice)}
                      </td>
                      <td className="tw-px-4 tw-py-3">
                        {isFirst ? (
                          <span className="tw-text-gray-400">—</span>
                        ) : (
                          <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-green-100 tw-text-green-700">
                            -{percentageSavings.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="tw-px-6 tw-pb-5 tw-pt-2 tw-text-center">
          <button
            className="tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-gray-600 tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
            onClick={onClose}
          >
            Compris !
          </button>
        </div>
      </div>
    </div>
  );
}
